import React from "react";
// ANT DESIGN COMPONENTS
import { Alert, Descriptions, Modal, Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { updateOpenModalOfflineCredential } from "../../../redux/users/userSlice";
// INTERFACES
import { RootState } from "../../../app/store";

export interface OfflineCredentialStatus {
  offline_password_set?: boolean;
  offline_password_is_issued?: boolean;
  password_changed_at?: string;
}

interface UsersModalOfflineCredentialProps {
  credential: OfflineCredentialStatus | null;
}

/**
 * Describes a user's offline credential without disclosing it.
 *
 * This replaces a dialog that displayed `offline_password` from the server and
 * offered it on the clipboard. That value was the stored argon2id hash, not a
 * password: it could not be given to anyone, would not sign anybody in, and
 * handing it out gave away material an attacker can grind offline. The server
 * no longer returns it.
 *
 * What an administrator actually wants to know is whether this account can be
 * signed into offline and whether the password they issued is still the one in
 * force, so that is what this shows. When they need something shareable, the
 * answer is to issue a new password — which is a different action, because it
 * changes the credential rather than reading it.
 */
function UsersModalOfflineCredential({ credential }: UsersModalOfflineCredentialProps) {
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const { openModalOfflineCredential } = useSelector((state: RootState) => state.user);

  const isSet = credential?.offline_password_set === true;
  const isIssued = credential?.offline_password_is_issued === true;
  const changedAt = credential?.password_changed_at ?? "";

  const passwordStateTag = () => {
    if (!isSet) return <Tag color="default">Not set</Tag>;
    if (isIssued) return <Tag color="gold">Server-issued, not yet replaced</Tag>;
    return <Tag color="cyan">Chosen by the user</Tag>;
  };

  return (
    <Modal
      title="Offline credential"
      open={openModalOfflineCredential}
      onCancel={() => dispatch(updateOpenModalOfflineCredential(false))}
      footer={null}
    >
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Offline sign-in">
          {isSet ? <Tag color="green">Available</Tag> : <Tag color="red">Unavailable</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="Password">{passwordStateTag()}</Descriptions.Item>
        <Descriptions.Item label="Last changed">
          {changedAt === "" ? <span className="drawer__text">Never</span> : changedAt}
        </Descriptions.Item>
      </Descriptions>

      {isIssued && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message="Still using the password it was issued"
          description="This account cannot be used for anything else until the user replaces it at sign-in."
        />
      )}

      <Alert
        type="warning"
        showIcon
        style={{ marginTop: 16 }}
        message="The password itself cannot be shown"
        description={t("userManagement.offlinePasswordNotRetrievable")}
      />
    </Modal>
  );
}

export default UsersModalOfflineCredential;
